import { useContext, useRef, useState } from "react";
import QAResponseQuestionEntity from "../../../../../../domain/entities/qa_response_question.entity";
import ModalsContext from "../../../../../providers/modal/ModalsContext";
import ModalsContextType from "../../../../../../domain/providers/modal/ModalsContextType";
import LocalizationContext from "../../../../../providers/localization/LocalizationContext";
import LocalizationContextType from "../../../../../../domain/providers/localization/LocalizationContextType";
import EditQAResponseQuestionModalComponent from "./edit_qa_response/edit_qa_response_question_modal.component";
import di from "../../../../../../dependency_injection";
import UpdateResponseQuestionUseCase, { UpdateResponseQuestionUseCaseName } from "../../../../../../domain/use_cases/qa_resume/update_response_question.use_case";
import { Either, isLeft, right } from "fp-ts/lib/Either";
import KeyWordLocalization from "../../../../../utils/KeyWordLocalization";
import ErrorEntity from "../../../../../../domain/entities/error.entity";
import ResultIAAudioComponentProps from "./result_ia_audio.component.props";

const useResultIAAudioComponentViewModel = ({result, reload, humanAgent,}: ResultIAAudioComponentProps) => {
    const [expandedQuestions, setExpandedQuestions] = useState<(string | number)[]>([]);
    const [openedTranscription, setOpenedTranscription] = useState<(string | number)[]>([]);
    const [openedSummary, setOpenedSummary] = useState<(string | number)[]>([]);
    const { pushModalCustom, addToast } = useContext(ModalsContext) as ModalsContextType;
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const scrollRef = useRef<HTMLDivElement>(null);

    const _questionIsExpanded = (index: string) => expandedQuestions.includes(index);

    const handleOpenEditResponse = (response: QAResponseQuestionEntity | undefined, maxPoints: number) => {
        if (!response) return;

        pushModalCustom(
            "lg",
            i18n(KeyWordLocalization.ResultIaAudioComponentEditResponse),
            <EditQAResponseQuestionModalComponent
                response={response}
                maxPoints={maxPoints}
                onSave={(re)=>_handleSaveResponse(re, response)}
            />,
            { hideOnTap: false }
        );
    };

    const _handleSaveResponse = async (updatedResponse: QAResponseQuestionEntity, oldResponse: QAResponseQuestionEntity): Promise<Either<ErrorEntity, void>> => {
        console.log("udpatedResponse", {result, updatedResponse, oldResponse});
        const response = await di.get<UpdateResponseQuestionUseCase>(UpdateResponseQuestionUseCaseName).call(updatedResponse, result.id);
        if (isLeft(response)) {
            addToast(i18n(response.left.code ?? KeyWordLocalization.UnknownError), "error", {});
            return response;
        }
        else {
            oldResponse.short = updatedResponse.short;
            oldResponse.points = updatedResponse.points;
            oldResponse.justification = updatedResponse.justification;
            addToast(i18n(KeyWordLocalization.ResultIaAudioComponentUpdatedResponse), "success", {});
            await reload(updatedResponse);
            return right(undefined);
        }


    };

    const _toggleExpandedQuestions = (index: string) => {
        _scrollUp();
        if (_questionIsExpanded(index)) {
            setExpandedQuestions(expandedQuestions.filter((expanded) => expanded !== index));
        } else {
            setExpandedQuestions([...expandedQuestions, index]);
        }
    };

    const _scrollUp = () => {
        scrollRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const _onSetOpenedTranscription = (_: (string | number)[]) => {
        setOpenedTranscription(_);
        setExpandedQuestions([]);
        setOpenedSummary([]);
        _scrollUp();
    };

    const _onSetOpenedSummary = (_: (string | number)[]) => {
        setOpenedSummary(_);
        setOpenedTranscription([]);
        setExpandedQuestions([]);
        _scrollUp();
    };

    const _onToggleCategory = (opened: (string | number)[]) => {
        setExpandedQuestions(opened);
        setOpenedTranscription([]);
        setOpenedSummary([]);
        _scrollUp();
    };

    return {
        scrollRef,
        expandedQuestions,
        openedTranscription,
        openedSummary,
        _questionIsExpanded,
        _toggleExpandedQuestions,
        _scrollUp,
        _onSetOpenedTranscription,
        _onSetOpenedSummary,
        _onToggleCategory,
        handleOpenEditResponse,
        i18n,
    };
};

export default useResultIAAudioComponentViewModel;